/**
 * Couche hors-ligne : instantané en cache, recherche locale, file de mouvements et synchro.
 * Les fonctions `sortie*` / `inventory*` tentent d'abord le réseau (via api.js) puis basculent
 * sur le cache local et la file d'attente en cas d'absence de réseau. Chaque mouvement porte
 * un identifiant client unique (`client_id`) pour garantir l'idempotence côté API à la synchro.
 */
import client, { sortieApi, inventoryApi } from '../services/api';
import { kvGet, kvSet, queueAdd, queueAll, queueRemove } from './idb';
import { toast } from 'sonner';

const SNAP_KEY = 'snapshot';

// Erreur réseau (aucune réponse HTTP) → notre intercepteur laisse `code` indéfini.
export const isNetworkError = (e) => !e || e.code === undefined || e.code === 0;

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `c-${Date.now()}-${Math.random().toString(16).slice(2)}`);
const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function queueChanged() { window.dispatchEvent(new Event('vd-queue-changed')); }

// --- Instantané ---
export async function saveSnapshot(s) { await kvSet(SNAP_KEY, { ...s, cached_at: Date.now() }); }
export async function getSnapshot() { return kvGet(SNAP_KEY); }

export async function refreshSnapshot() {
  const snap = await client.get('/sync/snapshot').then((r) => r.data);
  await saveSnapshot(snap);
  return snap;
}

// --- Recherche locale (Sortie) ---
async function localSortieSearch(q) {
  const snap = await getSnapshot();
  let refs = (snap?.sortie_refs || []).filter((r) => r.quantity > 0);
  const terms = norm(q).split(/\s+/).filter(Boolean);
  if (terms.length) {
    refs = refs.filter((r) => {
      const hay = norm(`${r.name} ${r.domain_name} ${r.year} ${r.format} ${r.format_label} ${r.color}`);
      return terms.every((t) => hay.includes(t));
    });
  }
  return refs.sort((a, b) => `${a.name}`.localeCompare(b.name)).slice(0, 30);
}

export async function sortieSearch(q) {
  if (navigator.onLine) {
    try { return await sortieApi.search(q); }
    catch (e) { if (!isNetworkError(e)) throw e; }
  }
  const query = (q || '').trim();
  const snap = await getSnapshot();
  const results = query ? await localSortieSearch(query) : (snap?.sortie_refs || []).filter((r) => r.quantity > 0).slice(0, 6);
  return { results, frequent: !query, offline: true };
}

export async function sortieRecent() {
  if (navigator.onLine) { try { return await sortieApi.recent(); } catch (e) { if (!isNetworkError(e)) throw e; } }
  const q = await queueAll();
  return q.filter((o) => o.kind === 'exit').slice(-4).reverse().map((o) => ({
    movement_id: o.client_id, date: new Date(o.created_at).toISOString(), label: o.label,
    format_label: '', quantity: -o.payload.quantity, motif: o.payload.motif,
    motif_label: 'en attente', undoable: true,
  }));
}

// --- Sortie hors-ligne ---
async function offlineExit(body) {
  const snap = await getSnapshot();
  const ref = (snap?.sortie_refs || []).find((r) => r.reference_id === body.reference_id);
  let newQty = ref ? ref.quantity : 0;
  if (body.motif !== 'verre') { newQty = Math.max(0, (ref?.quantity || 0) - body.quantity); if (ref) ref.quantity = newQty; }
  if (snap) await saveSnapshot(snap);
  await queueAdd({ client_id: body.client_id, kind: 'exit', payload: body, created_at: Date.now(), label: ref ? `${ref.name} ${ref.year}` : 'Sortie' });
  queueChanged();
  return { movement_id: body.client_id, new_quantity: newQty, unit_price: null, offline: true };
}

export async function sortieExit(payload) {
  const body = { ...payload, client_id: payload.client_id || uid() };
  if (navigator.onLine) { try { return await sortieApi.exit(body); } catch (e) { if (!isNetworkError(e)) throw e; } }
  return offlineExit(body);
}

async function offlineUndo(clientId) {
  const snap = await getSnapshot();
  const q = await queueAll();
  const op = q.find((o) => o.client_id === clientId);
  if (!op) return { undone: false };
  if (op.kind === 'exit' && snap) {
    const ref = (snap.sortie_refs || []).find((r) => r.reference_id === op.payload.reference_id);
    if (ref && op.payload.motif !== 'verre') { ref.quantity += op.payload.quantity; await saveSnapshot(snap); }
  }
  await queueRemove(clientId);
  queueChanged();
  return { undone: true };
}

export async function sortieUndo(movementId) {
  const q = await queueAll();
  if (q.some((o) => o.client_id === movementId)) return offlineUndo(movementId);
  return sortieApi.undo(movementId);
}

// --- Inventaire hors-ligne ---
function zonesFromRefs(refs) {
  const agg = {};
  refs.filter((r) => r.theoretical > 0).forEach((r) => {
    const parts = (r.location_code || '—').split('·');
    const levels = new Set([parts[0].trim()]);
    if (parts.length >= 2) levels.add(parts.slice(0, 2).map((p) => p.trim()).join('·'));
    levels.forEach((code) => {
      const z = agg[code] || (agg[code] = { code, references: 0, bottles: 0 });
      z.references += 1; z.bottles += r.theoretical;
    });
  });
  return Object.values(agg).sort((a, b) => a.code.localeCompare(b.code));
}

export async function inventoryZones() {
  if (navigator.onLine) { try { return await inventoryApi.zones(); } catch (e) { if (!isNetworkError(e)) throw e; } }
  const snap = await getSnapshot();
  return { zones: zonesFromRefs(snap?.inventory_refs || []), last_inventory: snap?.last_inventory || null };
}

export async function inventoryReferences(zone) {
  if (navigator.onLine) { try { return await inventoryApi.references(zone); } catch (e) { if (!isNetworkError(e)) throw e; } }
  const snap = await getSnapshot();
  return (snap?.inventory_refs || [])
    .filter((r) => (r.location_code || '').startsWith(zone))
    .sort((a, b) => (a.location_code || '').localeCompare(b.location_code || ''));
}

export async function inventoryValidate(payload) {
  const body = { ...payload, client_id: payload.client_id || uid() };
  if (navigator.onLine) { try { return await inventoryApi.validate(body); } catch (e) { if (!isNetworkError(e)) throw e; } }
  const snap = await getSnapshot();
  const changes = body.counts.filter((c) => {
    const ir = (snap?.inventory_refs || []).find((r) => r.reference_id === c.reference_id);
    return ir && c.counted !== ir.theoretical;
  });
  if (snap) {
    body.counts.forEach((c) => {
      const ir = (snap.inventory_refs || []).find((r) => r.reference_id === c.reference_id);
      if (ir) ir.theoretical = c.counted;
      const sr = (snap.sortie_refs || []).find((r) => r.reference_id === c.reference_id);
      if (sr) sr.quantity = c.counted;
    });
    await saveSnapshot(snap);
  }
  await queueAdd({ client_id: body.client_id, kind: 'inventory', payload: body, created_at: Date.now(), label: `Inventaire ${body.zone}` });
  queueChanged();
  return { group_id: body.client_id, count: changes.length, offline: true };
}

// --- Rangement (déplacement d'emplacement) hors-ligne ---
// Applique en ligne si possible, sinon met en file (idempotence via client_id).
export async function placeLocations(refId, body) {
  const payload = { ...body, client_id: body.client_id || uid() };
  if (navigator.onLine) {
    try { return await client.post(`/cave/references/${refId}/locations`, payload).then((r) => r.data); }
    catch (e) { if (!isNetworkError(e)) throw e; }
  }
  await queueAdd({ client_id: payload.client_id, kind: 'place', payload: { reference_id: refId, ...payload },
    created_at: Date.now(), label: `Déplacement ${payload.to_label || ''}`.trim() });
  queueChanged();
  return { offline: true, group_id: payload.client_id };
}

// --- File d'attente & synchronisation ---
export async function pendingCount() { return (await queueAll()).length; }

export async function flushQueue() {
  const q = await queueAll();
  let synced = 0;
  for (const op of q) {
    try {
      if (op.kind === 'exit') await client.post('/sortie/exit', op.payload);
      else if (op.kind === 'inventory') await client.post('/inventory/validate', op.payload);
      else if (op.kind === 'entry') await client.post('/scanner/entry', op.payload);
      else if (op.kind === 'place') await client.post(`/cave/references/${op.payload.reference_id}/locations`, op.payload);
      await queueRemove(op.client_id);
      synced += 1;
    } catch (e) {
      if (isNetworkError(e)) break; // toujours hors-ligne : on réessaiera
      // Rejet métier (réf. supprimée, etc.) : on retire de la file mais on prévient l'utilisateur.
      await queueRemove(op.client_id);
      toast.error(`Mouvement non synchronisé (${op.label || op.kind}) : ${e.message || 'refusé par le serveur'}`);
    }
  }
  if (synced) queueChanged();
  return synced;
}
